import React, { useState, useEffect } from "react";
import { useMutation, gql } from "@apollo/client";
import {
  Box,
  Button,
  Card,
  CardContent,
  FormControlLabel,
  Grid,
  Switch,
  TextField,
  Typography,
  CircularProgress,
  Divider,
} from "@mui/material";
import { Save as SaveIcon } from "@mui/icons-material";

const UPDATE_BOT_SETTINGS = gql`
  mutation UpdateBotSettings($widgetId: ID!, $settings: BotSettingsInput!) {
    updateBotSettings(widgetId: $widgetId, settings: $settings) {
      enabled
      greetingMessage
      personality
      ragEnabled
    }
  }
`;

function BotSettingsPanel({
  widgetId,
  botSettings,
  loading,
  onRefetch,
  showAlert,
}) {
  const [formData, setFormData] = useState({
    enabled: false,
    greetingMessage: "Hello! How can I help you today?",
    personality: "friendly and professional",
    ragEnabled: false,
  });
  const [hasChanges, setHasChanges] = useState(false);

  const [updateSettings, { loading: saving }] = useMutation(
    UPDATE_BOT_SETTINGS,
    {
      onCompleted: () => {
        showAlert("Bot settings saved successfully", "success");
        setHasChanges(false);
        onRefetch?.();
      },
      onError: (error) => {
        showAlert(`Error saving settings: ${error.message}`, "error");
      },
    }
  );

  // Initialize form data from props
  useEffect(() => {
    if (botSettings) {
      setFormData({
        enabled: botSettings.enabled ?? false,
        greetingMessage:
          botSettings.greetingMessage ?? "Hello! How can I help you today?",
        personality: botSettings.personality ?? "friendly and professional",
        ragEnabled: botSettings.ragEnabled ?? false,
      });
    }
  }, [botSettings]);

  const handleChange = (field) => (event) => {
    const value =
      event.target.type === "checkbox"
        ? event.target.checked
        : event.target.value;
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    setHasChanges(true);
  };

  const handleSave = () => {
    updateSettings({
      variables: {
        widgetId,
        settings: formData,
      },
    });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Bot Settings
      </Typography>
      <Typography variant="body2" color="textSecondary" paragraph>
        Configure the basic settings for your AI Bot.
      </Typography>

      <Grid container spacing={3}>
        {/* Enable/Disable Bot */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Box>
                  <Typography variant="subtitle1" fontWeight="bold">
                    Enable AI Bot
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    When enabled, the AI Bot will automatically respond to
                    customer messages
                  </Typography>
                </Box>
                <Switch
                  checked={formData.enabled}
                  onChange={handleChange("enabled")}
                  color="primary"
                  size="medium"
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* RAG Enable */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Box>
                  <Typography variant="subtitle1" fontWeight="bold">
                    Enable RAG (Knowledge Base)
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Enable retrieval-augmented generation to answer questions
                    using your knowledge base
                  </Typography>
                </Box>
                <Switch
                  checked={formData.ragEnabled}
                  onChange={handleChange("ragEnabled")}
                  color="primary"
                  size="medium"
                  disabled={!formData.enabled}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Greeting Message */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Greeting Message
              </Typography>
              <Typography variant="body2" color="textSecondary" paragraph>
                The first message the bot sends when a conversation starts
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={3}
                value={formData.greetingMessage}
                onChange={handleChange("greetingMessage")}
                placeholder="Hello! How can I help you today?"
                variant="outlined"
                disabled={!formData.enabled}
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Bot Personality */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Bot Personality
              </Typography>
              <Typography variant="body2" color="textSecondary" paragraph>
                Describe the tone and personality of your bot (e.g., "friendly
                and professional", "formal and concise")
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={2}
                value={formData.personality}
                onChange={handleChange("personality")}
                placeholder="friendly and professional"
                variant="outlined"
                disabled={!formData.enabled}
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Save Button */}
        <Grid item xs={12}>
          <Divider sx={{ my: 2 }} />
          <Box display="flex" justifyContent="flex-end">
            <Button
              variant="contained"
              color="primary"
              startIcon={
                saving ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <SaveIcon />
                )
              }
              onClick={handleSave}
              disabled={saving || !hasChanges}
            >
              {saving ? "Saving..." : "Save Settings"}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}

export default BotSettingsPanel;
